import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CATEGORIES } from "../lib/categories";
import { Briefcase, MapPin, Calendar as CalIcon, DollarSign, Plus, X, Target, Clock } from "lucide-react";
import { toast } from "sonner";

const COUNTRIES = ["Madagascar", "Sénégal", "Nigeria", "Côte d'Ivoire", "Cameroun", "RDC", "Mali", "Afrique du Sud", "Kenya", "Ghana", "Maroc", "Tunisie", "France", "Autre"];
const OPP_TYPES = [
  { k: "audition", l: "Casting / Audition" },
  { k: "gig", l: "Concert / Performance" },
  { k: "tour", l: "Tournée" },
  { k: "clip", l: "Clip vidéo" },
  { k: "event", l: "Évènement / Soirée" },
  { k: "shoot", l: "Shooting / Défilé" },
  { k: "other", l: "Autre opportunité" },
];

export const CastingsPage = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("all");
  const [country, setCountry] = useState("Tous");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", category: "music", opportunity_type: "audition",
    country: "Madagascar", city: "", deadline: "", budget: "", requirements: "", contact_email: user?.email || "",
  });

  const load = () => {
    const params = {};
    if (category !== "all") params.category = category;
    if (country !== "Tous") params.country = country;
    api.get("/castings", { params }).then(({ data }) => setItems(data)).catch(() => {});
  };

  useEffect(() => { load(); }, [category, country]);
  useEffect(() => { if (user?.email) setForm((f) => ({ ...f, contact_email: user.email })); }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/castings", form);
      toast.success("Annonce publiée !");
      setShowForm(false);
      setForm({ title: "", description: "", category: "music", opportunity_type: "audition", country: "Madagascar", city: "", deadline: "", budget: "", requirements: "", contact_email: user?.email || "" });
      load();
    } catch { toast.error("Erreur"); }
  };

  return (
    <div className="px-4 md:px-12 py-10 max-w-7xl mx-auto pb-32" data-testid="castings-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
        <div>
          <p className="inline-flex items-center gap-2 tag-pill mb-5 bg-brand-red/10 border-brand-red/40 text-brand-red">
            <Briefcase size={12} /> Appel à candidatures
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-balance">
            Appel à <span className="bg-gradient-to-r from-brand-red to-brand-yellow bg-clip-text text-transparent">candidatures</span>
          </h1>
          <p className="text-white/70 text-lg mt-3 max-w-2xl">
            Labels, agences, organisateurs, réalisateurs : trouvez le talent parfait. Artistes : saisissez les opportunités.
          </p>
        </div>
        {user && (
          <button onClick={() => setShowForm(true)} className="btn-primary inline-flex items-center gap-2 w-max" data-testid="casting-new-btn">
            <Plus size={14} /> Publier une annonce
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="castings-category-filter">
        <button onClick={() => setCategory("all")} className={`tag-pill ${category === "all" ? "active" : ""}`} data-testid="casting-filter-cat-all">🌍 Toutes catégories</button>
        {CATEGORIES.map((c) => (
          <button key={c.key} onClick={() => setCategory(c.key)} className={`tag-pill ${category === c.key ? "active" : ""}`} data-testid={`casting-filter-cat-${c.key}`}>{c.emoji} {c.label}</button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mb-10">
        <button onClick={() => setCountry("Tous")} className={`tag-pill ${country === "Tous" ? "active" : ""}`} data-testid="casting-filter-country-Tous">Tous pays</button>
        {COUNTRIES.map((c) => (
          <button key={c} onClick={() => setCountry(c)} className={`tag-pill ${country === c ? "active" : ""}`} data-testid={`casting-filter-country-${c}`}>{c}</button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-12 text-center">
          <Target size={32} className="mx-auto text-white/40 mb-3" />
          <p className="text-white/70 mb-4">Aucune opportunité pour ces critères.</p>
          {user && (
            <button onClick={() => setShowForm(true)} className="btn-primary inline-flex items-center gap-2">
              <Plus size={14} /> Être le premier à publier
            </button>
          )}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5 stagger">
          {items.map((c) => (
            <Link
              key={c.casting_id}
              to={`/casting/${c.casting_id}`}
              className="group rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-brand-red/30 transition-colors p-6"
              data-testid={`casting-card-${c.casting_id}`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-orange bg-brand-orange/10 border border-brand-orange/20 rounded-full px-3 py-1">
                  {CATEGORIES.find((x) => x.key === c.category)?.emoji} {CATEGORIES.find((x) => x.key === c.category)?.label}
                </span>
                <span className="text-[10px] uppercase tracking-widest text-white/50">
                  {OPP_TYPES.find((x) => x.k === c.opportunity_type)?.l || c.opportunity_type}
                </span>
              </div>
              <h3 className="font-display text-xl font-black mb-2 group-hover:text-brand-red transition-colors">{c.title}</h3>
              <p className="text-sm text-white/70 line-clamp-2 mb-4">{c.description}</p>
              <div className="flex flex-wrap items-center gap-3 text-xs text-white/60">
                <span className="inline-flex items-center gap-1"><MapPin size={12} />{c.city || "—"}, {c.country}</span>
                {c.deadline && <span className="inline-flex items-center gap-1"><CalIcon size={12} />Avant le {new Date(c.deadline).toLocaleDateString("fr-FR")}</span>}
                {c.budget && <span className="inline-flex items-center gap-1"><DollarSign size={12} />{c.budget}</span>}
                <span className="inline-flex items-center gap-1 ml-auto text-brand-yellow"><Clock size={12} />{c.applications_count} candidatures</span>
              </div>
              <p className="text-[11px] text-white/40 mt-3">Par {c.recruiter_name}</p>
            </Link>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center p-4" onClick={() => setShowForm(false)} data-testid="casting-form-modal">
          <div onClick={(e) => e.stopPropagation()} className="w-full md:max-w-2xl bg-brand-surface border border-white/10 rounded-2xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-2xl font-black">Nouvelle opportunité</h3>
              <button onClick={() => setShowForm(false)} className="text-white/50 hover:text-white"><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <Field label="Titre *"><Input value={form.title} onChange={(v) => setForm({ ...form, title: v })} required placeholder="Ex: Recherche rappeur pour clip" testid="cast-title" /></Field>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Catégorie *">
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none" data-testid="cast-category-select">
                    {CATEGORIES.map((c) => <option key={c.key} value={c.key} className="bg-brand-dark">{c.emoji} {c.label}</option>)}
                  </select>
                </Field>
                <Field label="Type d'opportunité *">
                  <select value={form.opportunity_type} onChange={(e) => setForm({ ...form, opportunity_type: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none" data-testid="cast-type-select">
                    {OPP_TYPES.map((t) => <option key={t.k} value={t.k} className="bg-brand-dark">{t.l}</option>)}
                  </select>
                </Field>
                <Field label="Pays">
                  <select value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none" data-testid="cast-country-select">
                    {COUNTRIES.map((c) => <option key={c} value={c} className="bg-brand-dark">{c}</option>)}
                  </select>
                </Field>
                <Field label="Ville"><Input value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="cast-city" /></Field>
                <Field label="Deadline"><Input type="date" value={form.deadline} onChange={(v) => setForm({ ...form, deadline: v })} testid="cast-deadline" /></Field>
                <Field label="Budget / Cachet"><Input value={form.budget} onChange={(v) => setForm({ ...form, budget: v })} placeholder="Ex: 500 000 Ar, selon profil..." testid="cast-budget" /></Field>
              </div>
              <Field label="Description *">
                <textarea required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none min-h-[100px]" placeholder="Décrivez l'opportunité en détail..." data-testid="cast-desc-input" />
              </Field>
              <Field label="Critères recherchés (optionnel)">
                <textarea value={form.requirements} onChange={(e) => setForm({ ...form, requirements: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none min-h-[80px]" placeholder="Ex: Homme 20-30 ans, expérience scène, disponible weekend..." data-testid="cast-req-input" />
              </Field>
              <Field label="Email de contact *"><Input type="email" required value={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} testid="cast-email" /></Field>
              <button type="submit" className="btn-primary w-full inline-flex items-center justify-center gap-2" data-testid="cast-submit-btn">
                <Plus size={14} /> Publier l'annonce
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, children }) => (<div><label className="block text-[10px] tracking-[0.25em] uppercase text-white/60 font-bold mb-2">{label}</label>{children}</div>);
const Input = ({ type = "text", value, onChange, placeholder, testid, required }) => (<input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} required={required} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-brand-red/50" data-testid={`${testid}-input`} />);

export default CastingsPage;
